import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useChartStyleNames } from '@/composables/useChartStyleNames';
import { fetchChartStyles } from '@/composables/fetchChartStyles';
import { parseChartStylesArray } from '@/composables/parseChartStyle';
import { INVALID_CHART_STYLE_RECID } from '@/composables/isValidChartStyleRecid';
import { sortObjectsClosure } from '@/utils/sortObjectsClosure';

let refreshChartStylesPromise: Promise<void> | null = null;

async function _refreshChartStyles() {
  const savedChartsStore = useSavedChartsStore();
  const { getChartStyleNameAndTemporaryName } = useChartStyleNames();

  try {
    const rawChartStyles = await fetchChartStyles();
    const chartStyles = parseChartStylesArray(rawChartStyles);

    chartStyles.sort(sortObjectsClosure({
      propertyName: 'htmlSanitizedName',
      order: 'ascending'
    }));

    chartStyles.forEach(chartStyle => {
      const { name, temporaryName } = getChartStyleNameAndTemporaryName(chartStyle);

      chartStyle.name = name;
      chartStyle.temporaryName = temporaryName;
    });

    const defaultChartStyle = chartStyles.find(cs => cs.recid === savedChartsStore.recidOfDefaultChartStyle);
    const recidOfDefaultChartStyle = defaultChartStyle?.recid ?? INVALID_CHART_STYLE_RECID;

    const chartStyleBeingPreviewedInChartLibraryListView = chartStyles.find(cs => cs.recid === savedChartsStore.recidOfChartStyleBeingPreviewedInChartLibraryListView);
    const recidOfChartStyleBeingPreviewedInChartLibraryListView = chartStyleBeingPreviewedInChartLibraryListView?.recid ?? INVALID_CHART_STYLE_RECID;

    const chartStyleBeingDeletedInModalView = chartStyles.find(cs => cs.recid === savedChartsStore.recidOfChartStyleBeingDeletedInModalView);
    const recidOfChartStyleBeingDeletedInModalView = chartStyleBeingDeletedInModalView?.recid ?? INVALID_CHART_STYLE_RECID;

    const chartStyleBeingEditedInModalView = chartStyles.find(cs => cs.recid === savedChartsStore.recidOfChartStyleBeingEditedInModalView);
    const recidOfChartStyleBeingEditedInModalView = chartStyleBeingEditedInModalView?.recid ?? INVALID_CHART_STYLE_RECID;

    savedChartsStore.$patch({
      chartStyles,
      recidOfDefaultChartStyle,
      recidOfChartStyleBeingPreviewedInChartLibraryListView,
      recidOfChartStyleBeingDeletedInModalView,
      recidOfChartStyleBeingEditedInModalView,
      isAttemptingToLoadChartStyles: false,
      hasAttemptedToLoadChartStyles: true,
      hasFailedToLoadChartStyles: false
    });
  } catch (e) {
    window.sc3WorkbenchLogger.error('Failed to refresh ChartStyles.', e);

    savedChartsStore.$patch({
      isAttemptingToLoadChartStyles: false,
      hasAttemptedToLoadChartStyles: true,
      hasFailedToLoadChartStyles: true
    });
  }
}

export async function refreshChartStyles() {
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    savedChartsStore.$patch({
      isAttemptingToLoadChartStyles: false,
      hasAttemptedToLoadChartStyles: true,
      hasFailedToLoadChartStyles: false
    });
    return;
  }

  let promiseObject;

  if (refreshChartStylesPromise) {
    // there's already a request to refresh the user's ChartStyles underway - await the current
    // request's promise rather than kicking off a new request
    promiseObject = refreshChartStylesPromise;
  } else {
    promiseObject = _refreshChartStyles();

    // store promise so that additional refreshChartStyles function calls that are
    // made while the current network call is still underway can await this promise
    // rather than inefficiently making their own duplicate network requests for
    // the same data
    refreshChartStylesPromise = promiseObject;

    promiseObject.then(() => {
      // remove the now-resolved promise from the cache
      refreshChartStylesPromise = null;
    });
  }

  await promiseObject;
}
